import React, { FC } from 'react';
import { useStorage } from '~Components/Storage';
import styled from '@emotion/styled';
import DeleteForeverIcon from '~Assets/icons/delete_forever.svg';
import { useButton, useLogger, useSelector } from '~Hooks';
import { useOrderStatusApi } from '~Components/OrderStatusUniverse';
import useI18n from '~Translations';
import { Box, RedButton, TransparentButton, Button } from '~Styles';
import useFilterOrders from '~Scenes/EventedKitchenDisplay/Hooks/useFilterOrders';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import useModal from '~ComponentsImproved/Modal/useModal';

const ActionButton = styled(TransparentButton)`
  white-space: nowrap;
`;

const CancelButton = styled(Button)`
  max-width: 160px;
  white-space: nowrap;
`;

interface Props {
  venueId: string;
}

const ClearOrders: FC<Props> = ({ venueId }) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const api = useOrderStatusApi(venueId);
  const logger = useLogger('kds-bottom-bar');
  const modal = useModal();

  const selectedKDSStation = useSelector((state) => state.stations)?.find(
    (s) => s.id === storage.selectedKDSStation,
  );

  const orders = useFilterOrders({ station: selectedKDSStation });

  const cancelButton = useButton({
    onClick: () => {
      modal.hide();
    },
  });

  const confirmButton = useButton({
    onClick: async () => {
      /** Progress all orders to COMPLETED */
      const ids = orders.map((o) => o.id);
      for (let i = 0; i < 3; i++) {
        ids.forEach((id) => api.progressOrder(id));
      }

      logger.info('Clearing all orders');
      modal.hide();
    },
  });

  const clearOrdersButton = useButton({
    onClick: () => {
      modal.show();
    },
    disabled: orders.length === 0,
  });

  return (
    <>
      <ActionButton {...clearOrdersButton}>{i18n('KDS.ClearOrders')}</ActionButton>

      <Modal isShowing={modal.isShowing} hide={modal.hide}>
        <ModalWrapper
          padding="large"
          header={{
            icon: DeleteForeverIcon,
            title: selectedKDSStation
              ? i18n('KDS.ClearOrders.ConfirmForStation')
              : i18n('KDS.ClearOrders.Confirm'),
          }}>
          <Box
            display="flex"
            flexDirection="row"
            gap="small"
            alignItems="center"
            fluid
            marginTop="small">
            <CancelButton {...cancelButton}>{i18n('General.Cancel')}</CancelButton>
            <RedButton fluid {...confirmButton}>
              {i18n('KDS.ClearOrders')}
            </RedButton>
          </Box>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default ClearOrders;
